import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import Authuser, { authCanPerform, evaluatePermissions, Role } from "@/types/base/authuser"
import { VALID_ENV_KEYS } from '@/types/base/valid-keys'
import { PermAction, PermOptions, permOptionsKey, TableWithPermissions } from '@/types/base/permissions'
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import { getWorkspaceById } from './workspaces'

const logger = createIsomorphicLogger('db.tenants')

export const getTenantById = async (supabase: SupabaseClient<Database>, tenantId: string) => {
  const { data: tenant, error } = await supabase
    .from("tenants")
    .select("*")
    .eq("id", tenantId)
    .single()

	if (!tenant) {
		logError(logger, error, 'Failed to fetch Tenant', { tenantId })
		throw new Error(error.message)
	}

  return tenant
}

export const getOptionalTenantById = async (supabase: SupabaseClient<Database>, tenantId: string) => {
  const { data: tenant, error } = await supabase
    .from("tenants")
    .select("*")
    .eq("id", tenantId)
    .maybeSingle()

  if (error) {
		logError(logger, error, 'Failed to fetch optional Tenant', { tenantId })
    throw new Error(error.message)
  }

  return tenant
}

export const getOptionalTenantByName = async (supabase: SupabaseClient<Database>, tenantName: string) => {
  const { data: tenant, error } = await supabase
    .from("tenants")
    .select("*")
    .eq("name", tenantName)
    .maybeSingle()

  if (error) {
		logError(logger, error, 'Failed to fetch optional Tenant', { tenantName })
    throw new Error(error.message)
  }

  return tenant
}

export const getOptionalTenantByStripeId = async (supabase: SupabaseClient<Database>, stripeId: string) => {
  const { data: tenant, error } = await supabase
    .from("tenants")
    .select("*")
    .eq("stripe_customer_id", stripeId)
    .maybeSingle()

  if (error) {
		logError(logger, error, 'Failed to fetch optional Tenant by Stripe ID', { stripeId })
    throw new Error(error.message)
  }

  return tenant
}

export const getAidonTenant = async (supabase: SupabaseClient<Database>) => {
  const { data: tenant, error } = await supabase
    .from("tenants")
    .select("*")
    .eq("name", "Aidon")
    .single()

	if (!tenant) {
		logError(logger, error, 'Failed to fetch Aidon Tenant')
		throw new Error(error.message)
	}

  return tenant
}

export const getAllTenants = async (supabase: SupabaseClient<Database>) => {
  const { data: tenants, error } = await supabase
    .from("tenants")
    .select("*")

  if (!tenants) {
	logError(logger, error, 'Failed to fetch Tenants')
    throw new Error(error.message)
  }

  return tenants
}

export const getAllTenantsWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'tenants'>[]> => {
	const allTenants = await getAllTenants(supabase)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'tenants'>[] = []

	for (const [i, tenant] of allTenants.entries()) {
		const perms = await evaluatePermissions(supabase, authuser,
				{ type: "tenants", context: "ANY", uuid: tenant.id })

		if (i === 0) { // Attach special non specific permission to first record only
		const options = { type: "tenants", action: PermAction.view, context: "Workspaces", ws: workspace } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const viewWsKey = permOptionsKey(options)
			perms[viewWsKey] = allowed
		}

		result.push({ ...tenant, permissions: perms })
	}

  return result
}

export const getTenantWorkspacesByTenantId = async (supabase: SupabaseClient<Database>, tenantId: string) => {
  const { data: workspaces, error } = await supabase
    .from("workspaces")
    .select("*")

  if (!workspaces) {
		logError(logger, error, 'Failed to fetch Tenant workspaces', { tenantId })
    throw new Error(error.message)
  }

  const tenantData = await getTenantById(supabase, tenantId)

	const tenantWorkspaces = {
		id: tenantData.id,
		name: tenantData.name,
		workspaces: workspaces
	}

	return tenantWorkspaces
}

export const createTenant = async (
	supabase: SupabaseClient<Database>,
  tenant: TablesInsert<"tenants">
) => {
  const { data: createdTenant, error } = await supabase
    .from("tenants")
    .insert([tenant])
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to create Tenant', { tenant: printObject(tenant) })
    throw new Error(error.message)
  }

  logger.info('Tenant created successfully', { tenantId: createdTenant.id })
  return createdTenant
}

export const createTenants = async (
	supabase: SupabaseClient<Database>,
  tenants: TablesInsert<"tenants">[]
) => {
  const { data: createdTenants, error } = await supabase
    .from("tenants")
    .insert(tenants)
    .select("*")

  if (error) {
		logError(logger, error, 'Failed to create Tenants', { tenants: printObject(tenants) })
    throw new Error(error.message)
  }

  logger.info('Tenants created successfully', { tenants: printObject(tenants) })
  return createdTenants
}

export const updateTenant = async (
	supabase: SupabaseClient<Database>,
  tenantId: string,
  tenant: TablesUpdate<"tenants">
) => {
  const { data: updatedTenant, error } = await supabase
    .from("tenants")
    .update(tenant)
    .eq("id", tenantId)
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to update Tenant', { tenant: printObject(tenant) })
    throw new Error(error.message)
  }

  return updatedTenant
}

export const deleteTenant = async (supabase: SupabaseClient<Database>, tenantId: string) => {
  const { error } = await supabase.from("tenants").delete().eq("id", tenantId)

  if (error) {
		logError(logger, error, 'Failed to delete Tenant', { tenantId })
    throw new Error(error.message)
  }

  return true
}

export type KeyValues = {
	[key: string]: string
}

export type CustomConfig = {
	apiKeys?: KeyValues
	sharedKeys?: KeyValues
	azureDeployments?: KeyValues
	irisTenantId?: string
	[key: string]: any // This allows other dynamic properties
}

export type Metadata = {
	customConfig?: CustomConfig
}

const extractCustomConfig = (metadata: Metadata, group: string, key: string): any | undefined => {
	// Ensure group is not 'apiKeys' as it has a different structure
	if (group === 'apiKeys' || group === 'sharedKeys') return undefined
	return metadata.customConfig?.[group]?.[key]
}

export const getApiKeys = (metadata: Metadata): KeyValues | undefined => {
	return metadata.customConfig?.apiKeys
}

export const getSharedKeys = (metadata: Metadata): KeyValues | undefined => {
	return metadata.customConfig?.sharedKeys
}

export const getAssistantDashboards = (metadata: Metadata): KeyValues | undefined => {
	return metadata.customConfig?.assistantDashboards
}

export const getAzureDeployments = (metadata: Metadata): KeyValues | undefined => {
	return metadata.customConfig?.azureDeployments
}

export const getIrisTenantId = (metadata: Metadata): string | undefined => {
	return metadata.customConfig?.irisTenantId
}

export const getValidApiKeys = (apiKeys: KeyValues | undefined, validKeys: typeof VALID_ENV_KEYS): KeyValues => {
	const validApiKeys: KeyValues = {}

	if (!apiKeys) return validApiKeys

	for (const [key, value] of Object.entries(apiKeys)) {
		if (Object.values(validKeys).includes(key as VALID_ENV_KEYS)) {
			validApiKeys[key] = value
		}
	}

	return validApiKeys
}

const convertToValidEnvKeysRecord = (apiKeys: KeyValues): Record<string, string> => {
	const result: Record<string, string> = {}

	for (const [key, value] of Object.entries(apiKeys)) {
		result[key] = value
	}

	return result
}

export const getValidEnvKeysRecords = (apiKeys: KeyValues | undefined, validKeys: typeof VALID_ENV_KEYS):
		Record<string, string> => {
	const validatedApiKeys = getValidApiKeys(apiKeys, validKeys)
	return convertToValidEnvKeysRecord(validatedApiKeys)
}

//Tenants can only be seen or worked on by root or Aidon admin. Aidon system tenant can't be delete by anyone
//Now tenant admin can see the tenant choice in the sidebar, see their tenant and edit description and JSON metadata
export const tenantCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	if (options.context == 'IMPEXP') {
		return isAidonAdmin(supabase, authuser)
	}
	else if (options.context == 'Folder') {
		return authuser.isAuthorizedForRole(Role.admin)
	}

	switch (options.action) {
		case PermAction.view:
			switch (options.context) {
				case "Workspaces":
					return false
				case "ANY":  //only to determine if can see the menu option
					return authuser.isAuthorizedForRole(Role.admin)
				default:
					return isAidonAdmin(supabase, authuser)
			}
		case PermAction.edit:
		case PermAction.save:
			if (options.uuid) {
				return authuser.isAuthorizedForRole(Role.admin)
			}
		case PermAction.delete:
			if (options.uuid) {
				try {
					const tenantData = await getTenantById(supabase, options.uuid)
					if (tenantData?.sharing == "system") {
						return false
					}
				} catch (error) {  //if not found we'll let it go
					logger.info("Tenant not found for id:" + options.uuid)
					return true
				}
			}
		default:  //includes create
			return isAidonAdmin(supabase, authuser)
	}
}

 /**
 *	Check if the user is an admin, doesn't indicate an admin for which tenant
 *
 * @param {Authuser} authuser
 * @return {*}
 */
export const isTenantAdmin = async (authuser: Authuser) => {
	return authuser.isAuthorizedForRole(Role.admin)
}

export const isAidonAdmin = async (supabase: SupabaseClient<Database>, authuser: Authuser) => {
	return authuser.isAuthorizedForRole(Role.admin) && await authuser.isTenantAidon(supabase)
}